// MACHINERY V2 (Stroje) — V4 shop-floor wall: the header's liveness dot +
// ticking clock (M2.14 fix pass).
//
// ============================================================
//  WHY THIS IS ITS OWN COMPONENT: THE 1 Hz TICK MUST NOT CROSS THE GRID.
// ============================================================
// `useWallClock` first landed at the ROOT of the wall tree (FleetWallView), so
// every one-second tick re-rendered the whole view: `machines.map(...)` rebuilt,
// `deriveCardPresentation` re-run per machine, and every WallTile (not
// memoised) reconciled. At ~400 machines that is thousands of elements
// reconciled every second, forever, on an unattended low-power stick PC that is
// meant to run for weeks. Holding the clock in a LEAF fixes it structurally:
// state that changes 1/s lives in the smallest subtree that renders it, and the
// grid re-renders only when telemetry or the machine list actually changes.
// The clock is still exactly ONE network-free 1 s timer — moved, not multiplied.
//
// ============================================================
//  THE DOT IS DRIVEN BY POLL HEALTH, NOT BY A CONSTANT.
// ============================================================
// It used to be `<ConnDot state="live" />` under a comment asserting "this dot
// says the wall is still polling" — an assertion the code did not implement.
// Nothing connected it to the poll, so it pulsed green while:
//   · the tab was hidden — `useFleetTelemetry` stops its interval on
//     `document.hidden` (fleetTelemetry.ts, the `onVisibility` handler,
//     personally re-read at commit time) while this clock keeps ticking, so a
//     backgrounded kiosk tab held twenty-minute-old tiles under a live dot;
//   · the gateway was failing — the fetch is fail-closed (SEC-REQ-9), so a 5xx
//     empties the map and every tile degrades to BEZ PRIPOJENIA while the
//     header still said "live".
// Now it renders green ONLY when both halves of the claim are true: the
// document is visible AND a gateway read actually resolved within the last two
// poll intervals. Otherwise it degrades to `longstale` — the faint grey dot
// with the X knockout, which is ConnDot's existing "do not trust this" mark.
// Two intervals, not one: a single interval would flicker on every ordinary
// round-trip latency, and one missed poll is not yet a dead wall.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import ConnDot from "../ConnDot";
import { FLEET_TELEMETRY_POLL_MS } from "../fleetTelemetry";
import { useWallClock } from "./useWallClock";

/** How long a successful read stays "recent enough to call the wall live". */
export const WALL_LIVE_GRACE_MS = FLEET_TELEMETRY_POLL_MS * 2;

export interface WallLiveClockProps {
  /** `FleetPageData.lastTelemetryOkAt` — 0 when nothing has ever resolved. */
  lastTelemetryOkAt: number;
}

const WallLiveClock: React.FC<WallLiveClockProps> = ({ lastTelemetryOkAt }) => {
  const { getCurrentLanguage } = useTranslation("myMachinery");
  const clock = useWallClock(getCurrentLanguage());

  // Recomputed on every clock tick — which is what makes the dot go dark on
  // its own, with no extra timer, roughly a second after the feed goes quiet.
  // `document.hidden` is read here rather than tracked in state for the same
  // reason: this component already re-renders once a second, so a second
  // subscription would buy nothing.
  const fresh =
    lastTelemetryOkAt > 0 && Date.now() - lastTelemetryOkAt < WALL_LIVE_GRACE_MS;
  const live = fresh && !document.hidden;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      {/* The PAGE's liveness, not a machine's — see this file's header. */}
      <ConnDot state={live ? "live" : "longstale"} size={8} />
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-2)" }}>
        {clock.time} · {clock.date}
      </span>
    </div>
  );
};

export default WallLiveClock;
